import { useCallback, useState } from 'react'
import * as Popover from '@radix-ui/react-popover'

import { ArrowUp, Sparkles } from '@/assets/icons'
import { kAgentV2LongInputWarningChars, kAgentV2MaxTurnInputChars } from '@/agent-v2/utils/agent-v2-contract'
import { IconButton } from '@/components/icon-button/IconButton'
import { InlineMessage } from '@/components/inline-message/InlineMessage'
import { kAgentV2InlineEditPresets } from '@/agent-v2/artifact-selection/inline-edit-presets'

type MatterArtifactSelectionPopoverProps = {
  anchorRect: DOMRect
  onDismiss: () => void
  onEscape?: () => void
  onSubmit: (instruction: string) => boolean | Promise<boolean>
}

function autoResize(textarea: HTMLTextAreaElement) {
  textarea.style.height = 'auto'
  textarea.style.height = `${textarea.scrollHeight}px`
}

export default function MatterArtifactSelectionPopover({ anchorRect, onDismiss, onEscape, onSubmit }: MatterArtifactSelectionPopoverProps) {
  const [instruction, setInstruction] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submissionError, setSubmissionError] = useState<string | null>(null)
  const trimmedInstruction = instruction.trim()
  const characterCount = trimmedInstruction.length
  const inputExceedsCap = characterCount > kAgentV2MaxTurnInputChars
  const showInputLengthGuidance = characterCount >= kAgentV2LongInputWarningChars
  const canSubmit = Boolean(trimmedInstruction) && !inputExceedsCap && !isSubmitting

  const submitInstruction = useCallback(
    async (instructionToSubmit: string) => {
      const trimmedInstructionToSubmit = instructionToSubmit.trim()
      if (!trimmedInstructionToSubmit || trimmedInstructionToSubmit.length > kAgentV2MaxTurnInputChars || isSubmitting) return

      setSubmissionError(null)
      setIsSubmitting(true)
      try {
        const submitted = await onSubmit(trimmedInstructionToSubmit)
        if (!submitted) {
          setSubmissionError('Unable to submit this edit. Please try again.')
        }
      } catch {
        setSubmissionError('Unable to submit this edit. Please try again.')
      } finally {
        setIsSubmitting(false)
      }
    },
    [isSubmitting, onSubmit]
  )

  return (
    <Popover.Root
      open
      onOpenChange={(open) => {
        if (!open) onDismiss()
      }}
    >
      <Popover.Anchor asChild>
        <div className="fixed" style={{ top: anchorRect.top, left: anchorRect.left, width: 0, height: anchorRect.height }} />
      </Popover.Anchor>
      <Popover.Portal>
        <Popover.Content
          align="start"
          className="z-50 w-[422px] max-w-[calc(100vw-2rem)] rounded-lg border border-brand-neutral-200 bg-white p-2 shadow-md"
          data-matter-artifact-selection
          onEscapeKeyDown={onEscape}
          onOpenAutoFocus={(event) => event.preventDefault()}
          side="bottom"
          sideOffset={8}
          collisionPadding={16}
        >
          <div className="group flex min-h-14 items-center gap-3 rounded-[9px] border border-brand-neutral-300 bg-white py-2 pr-2.5 pl-4 transition-colors focus-within:border-brand-400">
            <textarea
              aria-label="Edit selected passage"
              className="m-0 max-h-[clamp(6rem,30dvh,15rem)] flex-1 resize-none overflow-y-auto border-0 bg-transparent p-0 text-base leading-6 tracking-[-0.32px] text-brand-neutral-900 placeholder:text-brand-neutral-600 focus:ring-0"
              disabled={isSubmitting}
              onChange={(event) => {
                setInstruction(event.target.value)
                setSubmissionError(null)
                autoResize(event.target)
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault()
                  void submitInstruction(trimmedInstruction)
                }
              }}
              placeholder="Edit with a prompt"
              rows={1}
              value={instruction}
            />
            <IconButton
              aria-label="Submit edit"
              className="shrink-0 bg-true-gray-300 transition-colors group-focus-within:bg-brand-500"
              disabled={!canSubmit}
              icon={<ArrowUp />}
              onClick={() => void submitInstruction(trimmedInstruction)}
              size="lg"
              type="button"
            />
          </div>
          <div className="mt-0.5 flex flex-col gap-0.5">
            {kAgentV2InlineEditPresets.map((preset) => (
              <button
                key={preset.label}
                className="flex h-10 w-full items-center gap-2.5 rounded-md p-2 text-left text-sm leading-5 tracking-[-0.15px] text-brand-neutral-700 transition-colors hover:bg-brand-neutral-100 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={isSubmitting}
                onClick={() => void submitInstruction(preset.instruction)}
                type="button"
              >
                <Sparkles aria-hidden className="size-4 shrink-0 text-brand-neutral-700" />
                {preset.label}
              </button>
            ))}
          </div>
          {showInputLengthGuidance ? (
            <div className="mt-1">
              <InlineMessage variant={inputExceedsCap ? 'error' : 'neutral'}>
                {`${characterCount.toLocaleString('en-US')} / ${kAgentV2MaxTurnInputChars.toLocaleString('en-US')} characters`}
                {inputExceedsCap
                  ? ' — shorten your message or attach long source material as a document to submit.'
                  : ' — for better results, attach long source material as a document. You can still submit this edit.'}
              </InlineMessage>
            </div>
          ) : null}
          {submissionError ? (
            <div className="mt-1">
              <InlineMessage variant="error">{submissionError}</InlineMessage>
            </div>
          ) : null}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}
